import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { api } from "../api";

interface Props {
  onSuccess: () => void;
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#F9FAFB",
      fontFamily: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
      padding: "24px",
    }}>
      {children}
    </div>
  );
}

const card: React.CSSProperties = {
  background: "white",
  borderRadius: "4px",
  padding: "32px",
  boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
  border: "1px solid #F3F4F6",
  width: "100%",
  maxWidth: "440px",
};

/**
 * NotMe — confirmation page for the "that wasn't me" link in the
 * new-device-login email (see backend app/api/routes/not_me.py).
 *
 * This is the deliberately-inert half of the GET-then-POST split: loading
 * this page (the emailed GET link) does nothing but display who's affected,
 * via the read-only /auth/not-me/{token}/status lookup. The actual action
 * — revoke every session, kick off a password reset — only happens on the
 * explicit button click below, which POSTs to /auth/not-me/confirm. An
 * email security gateway that pre-fetches the link in the message therefore
 * can't trigger anything on the user's behalf; it just renders this page's
 * GET, no different from a human loading it and never clicking further.
 */
export default function NotMe({ onSuccess }: Props) {
  const { token } = useParams<{ token: string }>();
  const [email, setEmail] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) { setLoadError("This link is missing its token."); setLoading(false); return; }
    api.auth.notMeStatus(token)
      .then((d) => setEmail(d.email))
      .catch((e: any) => setLoadError(e.message || "This link is invalid or has expired."))
      .finally(() => setLoading(false));
  }, [token]);

  const handleConfirm = async () => {
    if (!token) return;
    setConfirming(true);
    setConfirmError(null);
    try {
      await api.auth.notMeConfirm(token);
      setDone(true);
    } catch (e: any) {
      setConfirmError(e.message || "Something went wrong. Please try again.");
      setConfirming(false);
    }
  };

  if (loading) {
    return (
      <Shell>
        <div style={card}>
          <p style={{ margin: 0, fontSize: "14px", color: "#6B7280" }}>Checking this link…</p>
        </div>
      </Shell>
    );
  }

  if (loadError) {
    return (
      <Shell>
        <div style={card}>
          <h2 style={{ margin: "0 0 12px", fontSize: "20px", fontWeight: 700, color: "#111827" }}>
            This link isn't valid
          </h2>
          <p style={{ margin: 0, fontSize: "14px", color: "#6B7280", lineHeight: 1.6 }}>
            {loadError} If you're worried about your account, use "Forgot password" from the
            sign-in page or reach out to your advisor directly.
          </p>
        </div>
      </Shell>
    );
  }

  if (done) {
    return (
      <Shell>
        <div style={card}>
          <h2 style={{ margin: "0 0 12px", fontSize: "20px", fontWeight: 700, color: "#111827" }}>
            Your account is secured
          </h2>
          <p style={{ margin: "0 0 20px", fontSize: "14px", color: "#6B7280", lineHeight: 1.6 }}>
            Every signed-in session was just logged out, and we've sent a password reset link to
            {email ? ` ${email}` : " your email"}. Use that link to set a new password.
          </p>
          <button
            onClick={onSuccess}
            style={{
              width: "100%", padding: "13px", background: "var(--brand-primary)", color: "white",
              border: "none", borderRadius: "4px", fontWeight: 700, fontSize: "14px",
              cursor: "pointer", fontFamily: "inherit",
            }}
          >
            Go to sign in
          </button>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div style={card}>
        <h2 style={{ margin: "0 0 4px", fontSize: "20px", fontWeight: 700, color: "#111827" }}>
          Wasn't you?
        </h2>
        <p style={{ margin: "0 0 20px", fontSize: "13px", color: "#9CA3AF", lineHeight: 1.55 }}>
          We noticed a sign-in to {email || "this account"} from a device we hadn't seen before. If
          that wasn't you, confirm below — we'll log out every device immediately and email you a
          link to set a new password.
        </p>

        {confirmError && (
          <div style={{
            background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: "4px",
            padding: "10px 12px", marginBottom: "16px", color: "#DC2626", fontSize: "13px",
          }}>
            {confirmError}
          </div>
        )}

        <button
          onClick={handleConfirm}
          disabled={confirming}
          style={{
            width: "100%", padding: "13px",
            background: confirming ? "#D1D5DB" : "#B91C1C",
            color: "white", border: "none", borderRadius: "4px",
            fontSize: "14px", fontWeight: 700,
            cursor: confirming ? "not-allowed" : "pointer",
            fontFamily: "inherit", marginBottom: "10px",
          }}
        >
          {confirming ? "Securing your account…" : "This wasn't me — log out every device"}
        </button>

        <p style={{ margin: 0, fontSize: "12px", color: "#9CA3AF", lineHeight: 1.5, textAlign: "center" }}>
          If this was you, no action is needed — you can close this page.
        </p>
      </div>
    </Shell>
  );
}
